import { faCartPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Stack, Tooltip, Typography } from '@vapor/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HicIconButton } from 'src/app/components/hic-vapor/button/icon-button'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import useInfoSnackBar from 'src/app/hooks/info-snack-bar/use-info-snack-bar'
import { ReservationCartRow, ReservationNewAdditionalService } from '../../form/reservation-new.type'
import AdditionalServicesCreationModal, { AdditionalServiceOption } from './additional-services-creation-modal'

type AdditionalServiceCellProps = {
  additionalService: ReservationNewAdditionalService
  cartsRow: ReservationCartRow[]
  disabled: boolean
  onCartRowChanges: (rows: ReservationCartRow[]) => void
}

export default function AdditionalServiceCell(props: AdditionalServiceCellProps): JSX.Element {
  const { additionalService, cartsRow, disabled, onCartRowChanges } = props
  const localizeString = useLocalizeString()
  const { t } = useTranslation()
  const { formatCurrency } = useFormatCurrency()
  const { md, lg } = useBreakpoints()
  const [open, setOpen] = useState(false)
  const infoSnackBar = useInfoSnackBar()

  const alreadyInTheCart = useMemo(() => {
    return props.cartsRow.find((row) => row.addedAdditionalServices?.find((as) => as.id === additionalService.additionalServiceId)) !== undefined
  }, [props.cartsRow])

  const enabledModalCartRows = useMemo(() => {
    return cartsRow.filter((row) => row.includedAdditionalServices?.find((includedAs) => includedAs?.id === additionalService.additionalServiceId) === undefined)
  }, [additionalService])

  const openAdditionalServiceModal = () => {
    const asIncludedInRow = cartsRow.flatMap((row) => row.includedAdditionalServices).filter((includedAs) => includedAs?.id === additionalService.additionalServiceId).length
    if (cartsRow.length === 0) {
      infoSnackBar(t('pms.booking.reservation_new.additional_services_empty_cart'))
    } else if (asIncludedInRow === cartsRow.length) {
      infoSnackBar(t('pms.booking.reservation_new.additional_services_already_included'))
    } else {
      setOpen(true)
    }
  }

  const additionalServiceDescription = useMemo(() => localizeString(additionalService.additionalService.description), [additionalService.additionalService.description])

  return (
    <>
      <Stack m={1} spacing={1} direction="row" justifyContent="flex-start" alignItems="center">
        <Stack direction="column" flexGrow={1}>
          <Typography variant="titleXSmall">
            <b>{localizeString(additionalService.additionalService.name)}</b>
          </Typography>
          <Typography variant="body" style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical' }}>
            {additionalServiceDescription}
          </Typography>
        </Stack>
        <Stack direction="column" flexShrink={2}>
          <Typography>
            <b>{formatCurrency(additionalService.additionalService.price, additionalService.additionalService.currency)}</b>
          </Typography>
          <Typography width="80px" variant="body">
            {t(`pms.booking.reservation_new.additional_services_pricing_type.${additionalService.additionalService.pricingType}`)}
          </Typography>
        </Stack>
        <Tooltip arrow title={t('pms.booking.reservation_new.add_service.tooltip')}>
          <div>
            <HicIconButton
              disabled={additionalService.notCompatibleWithCart || disabled}
              variant="contained"
              size="small"
              startIcon={<FontAwesomeIcon icon={faCartPlus} />}
              onClick={openAdditionalServiceModal}
              sx={{
                m: 0,
                paddingLeft: !md ? '0px !important' : undefined,
                paddingRight: !md ? '0px !important' : undefined,
                minWidth: !md ? '32px !important' : undefined,

                '& .MuiButton-startIcon': {
                  marginRight: !md ? '0px !important' : undefined,
                },
              }}
            >
              {md ? <Box sx={{ ml: 1 }}>{alreadyInTheCart ? t('pms.booking.reservation_new.edit_button') : t('pms.booking.reservation_new.choose_button')}</Box> : undefined}
            </HicIconButton>
          </div>
        </Tooltip>
      </Stack>

      <AdditionalServicesCreationModal
        open={open}
        handleClose={() => setOpen(false)}
        handleAddService={(additionalServices: AdditionalServiceOption[]) => {
          const changedRows: ReservationCartRow[] | undefined = additionalServices
            .map((additionalService) => {
              const row = cartsRow.find((row) => row.id === additionalService.cartId)
              if (row) {
                const tmpRow = { ...row }
                const additionalServiceToUpdateIndex = tmpRow.addedAdditionalServices?.findIndex((as) => as.id === additionalService.id) ?? -1

                if (tmpRow.addedAdditionalServices && additionalServiceToUpdateIndex >= 0) {
                  tmpRow.addedAdditionalServices[additionalServiceToUpdateIndex] = {
                    ...tmpRow.addedAdditionalServices[additionalServiceToUpdateIndex],
                    quantity: additionalService.quantity ?? undefined,
                    added: additionalService.added,
                  }
                } else {
                  const tmpAdditionalServices = [...(tmpRow.addedAdditionalServices ?? [])]
                  tmpAdditionalServices.push({
                    ...additionalService,
                  })
                  tmpRow.addedAdditionalServices = [...tmpAdditionalServices]
                }

                return tmpRow
              }
              return false
            })
            .filter((row) => row !== false) as ReservationCartRow[] | undefined
          if (changedRows) onCartRowChanges(changedRows)
          setOpen(false)
        }}
        additionalService={additionalService}
        cartResourceTypes={enabledModalCartRows.map((row) => {
          const rowHasAdditionalService = row.addedAdditionalServices?.find((as) => as.id === additionalService.additionalServiceId)
          const as = additionalService.additionalService
          return {
            cartId: row.id,
            resourceTypeName: typeof row.resourceType.name === 'string' ? row.resourceType.name : localizeString(row.resourceType.name),
            resourceTypeCode: row.resourceType.code,
            id: additionalService.additionalServiceId,
            priceZero: rowHasAdditionalService?.priceZero ?? false,
            adults: row.occupancy.adults,
            nights: row.nights,
            childrenOccupancy: row.occupancy.children ?? [],
            children: row.occupancy.children.length > 0 ? row.occupancy.children.map((child) => child.quantity).reduce((prev, current) => current + prev) : undefined,
            price: as.price,
            quantity: rowHasAdditionalService?.quantity,
            availability: rowHasAdditionalService?.availability,
            added: rowHasAdditionalService?.added ?? false,
            name: as.name,
            description: as.description,
            currency: as.currency,
            pricingType: as.pricingType,
            automaticForAdults: as.automaticForAdults,
            automaticForChildren: as.automaticForChildren,
            childPrices: as.childServicePrices,
            minStay: as.minStay ?? 1,
          }
        })}
      />
    </>
  )
}
